/* eslint-disable max-lines */
/**
 * Split-view transcript surface (#2237).
 *
 * ONE Virtuoso, one scroll container. Each row is a CSS grid of
 * source|translation; the taller side sets the row height so rows
 * stay aligned by utterance index without any pixel scroll sync.
 *
 * The surface replaces the old
 *   `TranscriptBody editorOnly readOnly` + `EditableSplitTranscriptPanel`
 * pair of Virtuosos that we used to keep in sync.
 *
 * Decisions in-code (#2237):
 * - Source cells use `SourceReadOnlyRow` (plain HTML, no Slate). The
 *   AC calls for "no caret" and "I cannot type"; dropping Slate
 *   avoids the whole class of accidental-edit bugs and keeps the
 *   file simple.
 * - Playback highlight runs on the translation side only (right
 *   cell), by not rendering source words inside a Slate editor.
 * - Mismatched utterance counts: both sides visible, the empty side
 *   shows `UnpairedRowBadge` (not pad, not merge).
 */

import {
  Components as VirtuosoComponents,
  Virtuoso,
  VirtuosoHandle
} from 'react-virtuoso';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';

import { useTranscript } from '@utils/providers/transcript-context.tsx';
import { useTranscriptHotkeys } from '@utils/providers/transcript-hotkey-context.tsx';
import { AnchoredOverlay } from '@components/editors/shared/find-replace/anchored-overlay.tsx';
import { TranscriptFindReplaceHost } from '@components/editors/transcript-editor/transcript-find-replace-host.tsx';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { Session } from '@utils/types/editors/sessions.ts';
import { StartStopWord } from '@utils/types/editors/utils.ts';
import type { ReactEditor } from 'slate-react';
import type { DocumentTranscriptUtteranceV1 } from '@utils/types/editors/transcripts/transcript.ts';
import { TranslationTranscriptProvider } from '@utils/providers/translation-transcript-context.tsx';
import { TagAssignmentsProvider } from '@utils/providers/tag-assignments-context.tsx';
import { TranscriptRow } from '@components/editors/transcript-editor/body/transcript-editor-row.tsx';
import { TranscriptTextStyleBox } from '@components/editors/transcript-editor/body/transcript-text-style-box.tsx';
import { EditorHistoryTools } from '@components/editors/shared/editor-history-tools.tsx';
import { CommentsProvider } from '@components/editors/shared/comments/comments-context.tsx';
import { CommentsGutter } from '@components/editors/shared/comments/comments-gutter.tsx';
import {
  SplitGutterItem,
  SPLIT_GUTTER_WIDTH
} from '@components/editors/shared/comments/split-gutter-item.tsx';
import { JumpToViewWord } from '@components/editors/jump-to-view-word.tsx';
import { SourceReadOnlyRow } from './source-readonly-row.tsx';
import { UnpairedRowBadge } from './unpaired-row-badge.tsx';

type Props = {
  session: Session;
  sessionId: string;
  translationDocumentId: string;
  sourceSearch?: string;
  onEditorFocus?: (editor: ReactEditor | null) => void;
  onStartStopWordChange?: (word: StartStopWord) => void;
};

export function SplitTranscriptSurface({
  session,
  sessionId,
  translationDocumentId,
  sourceSearch,
  onEditorFocus,
  onStartStopWordChange
}: Props) {
  const { document: sourceDocument, documentResult: sourceDocumentResult } =
    useTranscript();
  const sourceUtterances: DocumentTranscriptUtteranceV1[] = useMemo(
    () => sourceDocument?.document ?? [],
    [sourceDocument]
  );
  const sourceDocumentId = sourceDocumentResult?.result?.id ?? '';

  return (
    <TranslationTranscriptProvider
      key={translationDocumentId}
      session={session}
      sessionId={sessionId}
      documentId={translationDocumentId}
      onEditorFocus={onEditorFocus}
      onStartStopWordChange={onStartStopWordChange}
    >
      {/* Re-scope tag assignments to the translation doc — the
          page-level provider is scoped to the source doc, so without
          this the applied tag saves but never appears. */}
      <TagAssignmentsProvider
        sessionId={sessionId}
        documentId={translationDocumentId}
      >
        {/* EditorProvider lives at transcript-main scope — removing
            the inner wrapper so split-view rows register against the
            same `state.editors` the Find & Replace popover reads.
            Previously the popover's `replaceAllWordsInEditor` saw an
            empty editors map in split view and silently did nothing.
            `useEditorContext()` has no callers that need the
            inner-translation scope here (addEditor / removeEditor /
            trackModification / replaceSpeakersInEditor are the only
            consumers in the surface subtree). */}
        <SurfaceBody
          sessionId={sessionId}
          translationDocumentId={translationDocumentId}
          sourceDocumentId={sourceDocumentId}
          sourceUtterances={sourceUtterances}
          sourceSearch={sourceSearch}
        />
      </TagAssignmentsProvider>
    </TranslationTranscriptProvider>
  );
}

interface SurfaceBodyProps {
  sessionId: string;
  translationDocumentId: string;
  sourceDocumentId: string;
  sourceUtterances: DocumentTranscriptUtteranceV1[];
  sourceSearch?: string;
}

function SurfaceBody({
  sessionId,
  translationDocumentId,
  sourceDocumentId,
  sourceUtterances,
  sourceSearch
}: SurfaceBodyProps) {
  const { t } = useTranslation();
  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();
  const { currentTime } = useAudio();
  const {
    document: translationDocument,
    documentVersion,
    setSearch,
    popoverOpen,
    showJumpButton,
    setShowJumpButton,
    undo,
    redo,
    undoStack,
    redoStack,
    saveDocument,
    saveDocumentLoading,
    isDirty
  } = useTranscript();
  // Find & Replace in split view operates on the TRANSLATION document
  // because that's the editable pane; the source is read-only. The
  // outer popover mount in transcript-main runs with the source
  // `useTranscript()` scope, so matches would be scoped to source
  // text and Replace All would try to mutate editors the source side
  // doesn't even register. Rendering the popover HERE puts it inside
  // `TranslationTranscriptProvider`, which gives the FindReplaceHost
  // the translation document to query and the same hoisted
  // EditorProvider the translation editors register against.
  const { searchOpen, closeSearch } = useTranscriptHotkeys();
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollerRef = useRef<HTMLElement | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);

  // Forward the source's search string into the translation context
  // so Cmd+F highlights match on both sides. Must run in an effect:
  // calling the setter during render dispatches on every render and
  // churns the whole subtree, which unmounts the translation Slate
  // editor between keystrokes — caret vanishes, cut/paste miss.
  useEffect(() => {
    if (setSearch) setSearch(sourceSearch ?? '');
  }, [setSearch, sourceSearch]);

  const translationUtterances: DocumentTranscriptUtteranceV1[] = useMemo(
    () => translationDocument?.document ?? [],
    [translationDocument]
  );

  const rowCount = Math.max(
    sourceUtterances.length,
    translationUtterances.length
  );

  const handleScrollerRef = useCallback((el: HTMLElement | Window | null) => {
    if (el && el instanceof HTMLElement) scrollerRef.current = el;
  }, []);

  // #2237 — jump-to-current-word: rows drive showJumpButton; the
  // pill scrolls the translated row at the current playback time.
  const onWordOutOfView = useCallback(
    (_ref: HTMLSpanElement | null, direction: 'up' | 'down') => {
      setShowJumpButton(direction);
    },
    [setShowJumpButton]
  );
  const onWordInView = useCallback(() => {
    setShowJumpButton(false);
  }, [setShowJumpButton]);

  const jumpToCurrentWord = useCallback(() => {
    const ms = currentTime ? currentTime * 1000 : 0;
    const index = translationUtterances.findIndex(
      utt =>
        utt.start !== undefined &&
        utt.stop !== undefined &&
        ms >= utt.start &&
        ms <= utt.stop
    );
    if (index >= 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index,
        align: 'center',
        behavior: 'smooth'
      });
    }
    setShowJumpButton(false);
  }, [translationUtterances, currentTime, setShowJumpButton]);

  const renderRow = useCallback(
    (index: number) => {
      const sourceUtt = sourceUtterances[index];
      const translationUtt = translationUtterances[index];
      return (
        <div
          className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start"
          data-row-index={index}
        >
          <div>
            {sourceUtt ? (
              // #2246: source comments are shown here, read-only and in
              // a muted slate palette so the reader of the translation
              // can distinguish them from the pane's own amber/emerald
              // comments without hovering. The provider wraps only the
              // source cell so the translation side keeps resolving
              // `useCommentsContext()` to its own editable provider.
              <SourceCommentsProvider
                sessionId={sessionId}
                sourceDocumentId={sourceDocumentId}
              >
                <SourceReadOnlyRow utterance={sourceUtt} />
              </SourceCommentsProvider>
            ) : (
              <UnpairedRowBadge side="source" />
            )}
          </div>
          <div>
            {translationUtt ? (
              <TranscriptRow
                key={`split-trans-v${documentVersion}-${translationUtt.start}-${translationUtt.stop}`}
                index={index}
                paragraph={translationUtt}
                onWordOutOfView={onWordOutOfView}
                onWordInView={onWordInView}
              />
            ) : (
              <UnpairedRowBadge side="translation" />
            )}
          </div>
        </div>
      );
    },
    [
      sourceUtterances,
      translationUtterances,
      documentVersion,
      onWordOutOfView,
      onWordInView
    ]
  );

  const vComponents: VirtuosoComponents = useMemo(
    () => ({
      Item: ({ children, ...props }) => (
        <SplitGutterItem
          {...props}
          className="pr-3"
          style={{
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
            paddingLeft: '0.75rem'
          }}
        >
          {children}
        </SplitGutterItem>
      )
    }),
    []
  );

  return (
    <CommentsProvider
      sessionId={sessionId}
      documentId={translationDocumentId}
      ownershipLabel={t('translation')}
      documentUtterances={translationDocument?.document}
    >
      <div
        className="flex flex-col h-full min-h-0"
        style={vars}
        data-document-id={translationDocumentId}
        data-document-dirty={isDirty ?? false}
      >
        <div
          ref={containerRef}
          className="relative flex-1 min-h-0 m-2 flex flex-col rounded-xl border overflow-hidden shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]"
          style={{
            borderColor: getColour('border'),
            backgroundColor: getColour('secondLayer')
          }}
        >
          <header
            className="flex items-center justify-between gap-2 px-3 py-1 h-11 shrink-0 border-b"
            style={{ borderColor: getColour('border') }}
          >
            {/* #2289: undo/redo/save next to the formatting toolbox.
                Save stays always-clickable: Replace All goes through
                the editor-context layer, not through `editorUpdateParagraph`,
                so it mutates the document without flipping `isDirty`.
                Gating Save on `!isDirty` meant the button stayed
                disabled even after the user ran a Replace. */}
            <EditorHistoryTools
              undo={undo}
              redo={redo}
              save={() => saveDocument(true)}
              undoDisabled={saveDocumentLoading || undoStack.length === 0}
              redoDisabled={saveDocumentLoading || redoStack.length === 0}
              saveDisabled={saveDocumentLoading}
              saving={saveDocumentLoading}
              undoLabel={t('undo')}
              redoLabel={t('redo')}
              saveLabel={t('save')}
            />
            <TranscriptTextStyleBox
              containerRef={containerRef}
              popoverOpen={popoverOpen}
              sessionId={sessionId}
              documentId={translationDocumentId}
            />
          </header>

          <div className="relative flex-1 min-h-0 text-lg overflow-auto">
            {rowCount === 0 ? (
              <div className="flex items-center justify-center h-full p-8">
                <div className={cn('text-center opacity-60', cls.text)}>
                  <p className="text-sm">{t('no-segments-available')}</p>
                </div>
              </div>
            ) : (
              <Virtuoso
                ref={virtuosoRef}
                key={`split-surface-${translationDocumentId}-${rowCount}-${documentVersion}`}
                totalCount={rowCount}
                style={{ height: '100%', width: '100%', overflowX: 'hidden' }}
                components={vComponents}
                itemContent={renderRow}
                initialItemCount={Math.min(rowCount, 20)}
                // Match single-pane body: a tight buffer unmounts the
                // row being edited on scroll, blanking Slate's caret
                // and breaking cut/paste. See TranscriptBody.
                increaseViewportBy={3000}
                scrollerRef={handleScrollerRef}
              />
            )}
            {/* Narrower column in split view — each pane is already
                ~half the viewport, so a 320px gutter would eat the
                text. 220px keeps the gutter usable without compressing
                the reading area further. See #2244 scope. */}
            <CommentsGutter
              scrollRootRef={scrollerRef}
              columnWidth={SPLIT_GUTTER_WIDTH}
            />
          </div>
          <JumpToViewWord
            onJumpToWord={jumpToCurrentWord}
            visible={showJumpButton}
          />
        </div>
      </div>
      {searchOpen && (
        <AnchoredOverlay anchorKey="search">
          <TranscriptFindReplaceHost onClose={closeSearch} />
        </AnchoredOverlay>
      )}
    </CommentsProvider>
  );
}

/**
 * #2246 — thin wrapper that mounts a nested `CommentsProvider` scoped
 * to the SOURCE document, read-only and in the 'muted' palette. The
 * ticket's AC asks that source comments show on the translation view
 * so a translator can read the transcriber's notes, with these three
 * guardrails:
 *   1. Distinguishable at a glance (muted slate instead of amber)
 *   2. Read-only from the translation view (resolve/edit/delete gated
 *      by the provider's `readOnly` flag)
 *   3. Resolving a translation comment leaves source untouched (each
 *      row's provider scopes its own `useGetComments` query)
 *
 * `useGetComments` is keyed on `[session_id, document_id]`, so even
 * though the source provider mounts once per row, react-query dedupes
 * the fetch — one network request per split-view mount, cached across
 * every row.
 */
function SourceCommentsProvider({
  sessionId,
  sourceDocumentId,
  children
}: {
  sessionId: string;
  sourceDocumentId: string;
  children: React.ReactNode;
}) {
  return (
    <CommentsProvider
      sessionId={sessionId}
      documentId={sourceDocumentId}
      readOnly
      tone="muted"
    >
      {children}
    </CommentsProvider>
  );
}
